import { mobileApi, mobileRequest } from './mobileApi';

export const mobileChequeService = {
  getBanks: async () => {
    return mobileRequest(() => mobileApi.get('/banks'));
  },

  getBeneficiaries: async (params = {}) => {
    return mobileRequest(() => mobileApi.get('/beneficiarymaster', { params }));
  },

  getCheques: async (params = {}) => {
    return mobileRequest(() => mobileApi.get('/cheques', { params }));
  },

  getChequeById: async (id) => {
    return mobileRequest(() => mobileApi.get(`/printed-cheques/${id}`));
  },

  createCheque: async (data) => {
    return mobileRequest(() => mobileApi.post('/cheques', data));
  },

  getNextChequeNumber: async (bankId) => {
    return mobileRequest(() => mobileApi.get(`/cheques/next-number/${bankId}`));
  },

  getDashboardStats: async (params = {}) => {
    return mobileRequest(() => mobileApi.get('/cheques/dashboard-stats', { params }));
  },

  getCompanyPreferences: async () => {
    try {
      const response = await mobileApi.get('/companypreferences');
      const responseData = response.data;
      const data = responseData.Data || responseData.data || responseData;
      if (!data) return null;
      return {
        includeAcPayee: data.IncludeAcPayee ?? data.includeAcPayee ?? true,
        includeAmountPrefix: data.IncludeAmountPrefix ?? data.includeAmountPrefix ?? true,
        includeAmountSuffix: data.IncludeAmountSuffix ?? data.includeAmountSuffix ?? false,
        includeBeneficiary: data.IncludeBeneficiary ?? data.includeBeneficiary ?? true,
        includeDate: data.IncludeDate ?? data.includeDate ?? true,
        includeAmount: data.IncludeAmount ?? data.includeAmount ?? true,
        includeNonNegotiable: data.IncludeNonNegotiable ?? data.includeNonNegotiable ?? false,
        includeBeneficiary2: data.IncludeBeneficiary2 ?? data.includeBeneficiary2 ?? false,
        includeRemarks: data.IncludeRemarks ?? data.includeRemarks ?? false,
        chequeSizedPdf: data.ChequeSizedPdf ?? data.chequeSizedPdf ?? false,
        excludeCurrencyWords: data.ExcludeCurrencyWords ?? data.excludeCurrencyWords ?? false,
        enableSignedStatus: data.EnableSignedStatus ?? data.enableSignedStatus ?? false,
      };
    } catch {
      return null;
    }
  },

  printCheque: async (chequeId, printOptions = {}) => {
    try {
      // Build query params from print options
      const params = new URLSearchParams();
      const optionKeys = [
        'includeAcPayee', 'includeBeneficiary', 'includeDate', 'includeAmount',
        'includeNonNegotiable', 'includeAmountPrefix', 'includeAmountSuffix',
        'includeBeneficiary2', 'includeRemarks', 'chequeSizedPdf', 'excludeCurrencyWords'
      ];
      optionKeys.forEach(key => {
        if (printOptions[key] !== undefined) {
          params.append(key, printOptions[key]);
        }
      });
      const queryString = params.toString();
      const url = `/cheques/pdf/${chequeId}/GetChqPdf${queryString ? `?${queryString}` : ''}`;

      const response = await mobileApi.get(url, {
        responseType: 'blob',
        headers: { 'Accept': 'application/pdf' }
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);

      // Use anchor click — window.open() is blocked on mobile after async
      const link = document.createElement('a');
      link.href = blobUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.download = `cheque-${chequeId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
      return { success: true };
    } catch (error) {
      return { success: false, message: error.message || 'Failed to print cheque' };
    }
  },

  printChequeLetterSize: async (chequeId, printOptions = {}) => {
    try {
      // Build query params from print options
      const params = new URLSearchParams();
      const optionKeys = [
        'includeAcPayee', 'includeBeneficiary', 'includeDate', 'includeAmount',
        'includeNonNegotiable', 'includeAmountPrefix', 'includeAmountSuffix',
        'includeBeneficiary2', 'includeRemarks', 'excludeCurrencyWords'
      ];
      optionKeys.forEach(key => {
        if (printOptions[key] !== undefined) {
          params.append(key, printOptions[key]);
        }
      });
      const queryString = params.toString();
      const url = `/cheques/pdf/${chequeId}/GetChqPdfLetterSize${queryString ? `?${queryString}` : ''}`;

      const response = await mobileApi.get(url, {
        responseType: 'blob',
        headers: { 'Accept': 'application/pdf' }
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);

      // Use anchor click — window.open() is blocked on mobile after async
      const link = document.createElement('a');
      link.href = blobUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.download = `cheque-${chequeId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
      return { success: true };
    } catch (error) {
      return { success: false, message: error.message || 'Failed to print cheque' };
    }
  },

  voidCheque: async (chequeId, reason) => {
    return mobileRequest(() =>
      mobileApi.post(`/cheques/${chequeId}/cancel`, { reason })
    );
  },

  markCleared: async (chequeIds, clearedDate) => {
    return mobileRequest(() =>
      mobileApi.post('/cheques/mark-cleared', { chequeIds, clearedDate })
    );
  },

  // Optional Signed Status module
  markSigned: async (chequeIds) => {
    return mobileRequest(() =>
      mobileApi.post('/cheques/mark-signed', { chequeIds })
    );
  },

  markUnsigned: async (chequeIds) => {
    return mobileRequest(() =>
      mobileApi.post('/cheques/mark-unsigned', { chequeIds })
    );
  }
};
